import { MatterSubItemEventLogsContextMenu } from "@/components/features/matter/event-logs/MatterSubItemEventLogsContextMenu";
import { MatterSubItemEventLogsModal } from "@/components/features/matter/event-logs/MatterSubItemEventLogsModal";
import { MatterSubItemFileReferencesContextMenu } from "@/components/features/matter/file-repo/MatterSubItemFileReferencesContextMenu";
import { MatterSubItemFileReferencesModal } from "@/components/features/matter/file-repo/MatterSubItemFileReferencesModal";
import { FileReferences } from "@/components/features/matter/file-repo/FileReferences";
import {
  MatterSubItemFileReferenceUpload,
  type MatterSubItemFileReferenceUploadHandle,
} from "@/components/features/matter/file-repo/MatterSubItemFileReferenceUpload";
import { MatterSubItemNotesContextMenu } from "@/components/features/matter/notes/MatterSubItemNotesContextMenu";
import { MatterSubItemNotesModal } from "@/components/features/matter/notes/MatterSubItemNotesModal";
import { MatterSubItemTasksContextMenu } from "@/components/features/matter/tasks/MatterSubItemTasksContextMenu";
import { MatterSubItemTasksModal } from "@/components/features/matter/tasks/MatterSubItemTasksModal";
import { MatterExpenseUpdatesModal } from "@/components/features/matter/updates/MatterExpenseUpdatesModal";
import { MatterExpenseUpdatesContextMenu } from "@/components/features/matter/updates/MatterExpenseUpdatesContextMenu";
import { CreateObjectUpdateModal } from "@/components/features/matter/updates/CreateObjectUpdateModal";
import { MatterSubItemGenerateDocumentContextMenu } from "@/components/features/matter/context-menus/MatterSubItemGenerateDocumentContextMenu";
import { ActionConfirmationModal, ContextMenu } from "@/components/ui";
import { useUser } from "@/components/UserProvider";
import type { ExpenseViewModel } from "@/hooks/matter-view-models/use-expenses-view-model";
import { useGlobalModal } from "@/hooks/useGlobalModal";
import { useDeleteMatterExpense } from "@/lib/data/react-query/matters/matter-expenses";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import type { Matter } from "@/lib/types";
import { useRef, useState, type ReactNode } from "react";

interface MatterCostContextMenuProps {
  expense: ExpenseViewModel;
  matter: Matter;
  trigger: ReactNode;
}

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

export const MatterCostContextMenu = ({
  expense,
  matter,
  trigger,
}: MatterCostContextMenuProps) => {
  const { hasActionPermission } = useUser();
  const { openExpenseModal } = useGlobalModal();
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false);
  const [isEventLogsOpen, setIsEventLogsOpen] = useState(false);
  const fileReferenceUploadRef =
    useRef<MatterSubItemFileReferenceUploadHandle>(null);
  const [isFileReferencesOpen, setIsFileReferencesOpen] = useState(false);
  const [isCreateFileReferenceOpen, setIsCreateFileReferenceOpen] =
    useState(false);
  const [isNotesOpen, setIsNotesOpen] = useState(false);
  const [isUpdatesOpen, setIsUpdatesOpen] = useState(false);
  const [isCreateUpdateOpen, setIsCreateUpdateOpen] = useState(false);
  const [isTasksOpen, setIsTasksOpen] = useState(false);
  const deleteExpense = useDeleteMatterExpense();
  const canDeleteExpense = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterExpense,
    ACTION_PERMISSION_VALUES.delete,
  );
  const copyValue = [
    expense.label,
    expense.category ? `Category: ${expense.category}` : undefined,
    expense.cost != null
      ? `Original cost: ${currencyFormatter.format(expense.cost)}`
      : undefined,
    expense.paidAmount != null
      ? `Paid amount: ${currencyFormatter.format(expense.paidAmount)}`
      : undefined,
  ]
    .filter(Boolean)
    .join("\n");

  return (
    <>
      <ContextMenu
        entityName="cost"
        open={{
          onSelect: () => {
            openExpenseModal({
              matterId: expense.matterId,
              expenseId: expense.id,
              editing: true,
            });
          },
        }}
        copy={{
          value: copyValue,
          successLabel: "Cost details",
        }}
        delete={
          canDeleteExpense
            ? {
                disabled: deleteExpense.isPending,
                onSelect: () => {
                  setIsDeleteConfirmationOpen(true);
                },
              }
            : false
        }
        trigger={trigger}
      >
        <MatterSubItemGenerateDocumentContextMenu
          matter={matter}
          subItem={{ id: expense.id, type: "expense" }}
        />
        <MatterSubItemTasksContextMenu
          matter={matter}
          onOpenTasks={() => {
            setIsTasksOpen(true);
          }}
          subItem={{ id: expense.id, type: "expense" }}
        />
        <MatterSubItemNotesContextMenu
          matter={matter}
          onOpenNotes={() => {
            setIsNotesOpen(true);
          }}
          subItem={{ id: expense.id, type: "expense" }}
        />
        <MatterSubItemFileReferencesContextMenu
          onOpenFileReferences={() => {
            setIsFileReferencesOpen(true);
          }}
          onCreateFileReferences={() => {
            setIsCreateFileReferenceOpen(true);
          }}
          onUploadFileReference={() => {
            fileReferenceUploadRef.current?.openFilePicker();
          }}
        />
        <MatterExpenseUpdatesContextMenu
          onOpenUpdates={() => {
            setIsUpdatesOpen(true);
          }}
          onCreateUpdate={() => {
            setIsCreateUpdateOpen(true);
          }}
        />
        <MatterSubItemEventLogsContextMenu
          onOpenEventLogs={() => {
            setIsEventLogsOpen(true);
          }}
        />
      </ContextMenu>
      <ActionConfirmationModal
        open={isDeleteConfirmationOpen}
        setOpen={(open) => {
          if (!open && !deleteExpense.isPending) {
            setIsDeleteConfirmationOpen(false);
          }
        }}
        title={`Are you sure you want to delete "${expense.label}"?`}
        description="This action cannot be undone."
        buttonText="Delete cost"
        afterActionFn={async () => {
          await deleteExpense.mutateAsync({
            matterId: expense.matterId,
            id: expense.id,
          });
        }}
        isActionPending={deleteExpense.isPending}
      />
      <MatterSubItemTasksModal
        open={isTasksOpen}
        onOpenChange={setIsTasksOpen}
        entityLabel={expense.label}
        matter={matter}
        subItem={{ id: expense.id, type: "expense" }}
      />
      <MatterSubItemNotesModal
        open={isNotesOpen}
        onOpenChange={setIsNotesOpen}
        entityLabel={expense.label}
        matter={matter}
        subItem={{ id: expense.id, type: "expense" }}
      />
      {isUpdatesOpen ? (
        <MatterExpenseUpdatesModal
          open={isUpdatesOpen}
          onOpenChange={setIsUpdatesOpen}
          entityLabel={expense.label}
          expenseId={expense.id}
        />
      ) : null}
      {isCreateUpdateOpen ? (
        <CreateObjectUpdateModal
          open={isCreateUpdateOpen}
          onOpenChange={setIsCreateUpdateOpen}
          anchorObjectType="matter_expense"
          anchorObjectId={expense.id}
          entityLabel={expense.label}
        />
      ) : null}
      <MatterSubItemFileReferencesModal
        open={isFileReferencesOpen}
        onOpenChange={(open) => {
          setIsFileReferencesOpen(open);
          if (!open) {
            setIsCreateFileReferenceOpen(false);
          }
        }}
        createFileReferenceOpen={
          isFileReferencesOpen ? isCreateFileReferenceOpen : false
        }
        onCreateFileReferenceOpenChange={setIsCreateFileReferenceOpen}
        entityLabel={expense.label}
        matter={matter}
        subItem={{ id: expense.id, type: "expense" }}
      />
      <MatterSubItemEventLogsModal
        open={isEventLogsOpen}
        onOpenChange={setIsEventLogsOpen}
        entityLabel={expense.label}
        matter={matter}
        subItem={{ id: expense.id, type: "expense" }}
      />
      {isCreateFileReferenceOpen && !isFileReferencesOpen ? (
        <FileReferences
          matter={matter}
          subItem={{ id: expense.id, type: "expense" }}
          createOnly
          createFileReferenceOpen
          onCreateFileReferenceOpenChange={setIsCreateFileReferenceOpen}
        />
      ) : null}
      <MatterSubItemFileReferenceUpload
        ref={fileReferenceUploadRef}
        matter={matter}
        subItem={{ id: expense.id, type: "expense" }}
      />
    </>
  );
};
